import { Box, CircularProgress, Divider, Drawer, FormControl, IconButton, MenuItem, OutlinedInput, Select, TextField, Typography, Grid } from "@mui/material";
import { axios_tenant } from "../../../../axios";
import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import transformClientResponse from "../utils/transformClientResponse";
import { KOT_DISPLAY_DATE_FORMAT, formatKotDate, formatKotDateTime, parseKotStoredDate, toKotStorageDate } from "../utils/dateHelpers";
import TruncatedText from "../components/TruncatedText";

const ACTION_MAPPING = {
  CREATE: {
    clientName: "Client Legal Name",
    clientNumber: "Client Number",
    entityStatus: "Entity Status",
    assignment: "Assignment of Para 7 - US State",
  },
};

const formatFormatStr = (str) => {
  if (!str) return "General Profile";
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

const formatFieldLabel = (fieldName) => {
  if (fieldName === "matters") return "Family No(s)";
  return formatFormatStr(fieldName);
};

const ClientChanges = () => {
  const [loading, setLoading] = useState(false);
  const [trackedChanges, setTrackedChanges] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [section, setSection] = useState("");
  const [field, setField] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [clientHeader, setClientHeader] = useState({ name: "", number: "" });

  const stateData = location.state || {};

  const fullClientData = useSelector((state) => state.kotClient);
  const clientDetails = fullClientData?.clientDetails || {};

  // Prefer API data, then location.state, then Redux
  const clientName = clientHeader.name || stateData.clientName || clientDetails?.clientLegalName || clientDetails?.clientName;
  const clientNumber = clientHeader.number || stateData.clientNumber || clientDetails?.clientNumber;

  const displayTitle = clientName && clientNumber ? `${clientName} (${clientNumber})` : clientName || "Client Profile";

  const handleActionClick = (row) => {
    setSelectedRow(row);
    setOpenDrawer(true);
  };

  const availableSections = useMemo(() => {
    const uniq = new Set(trackedChanges.map((r) => r.sectionName || "General Profile"));
    return Array.from(uniq).sort((a, b) => formatFormatStr(a).localeCompare(formatFormatStr(b)));
  }, [trackedChanges]);

  const availableFields = useMemo(() => {
    const scoped = section ? trackedChanges.filter((r) => r.sectionName === section) : trackedChanges;
    const uniq = new Set(scoped.map((r) => r.fieldName).filter(Boolean));
    return Array.from(uniq).sort((a, b) => formatFieldLabel(a).localeCompare(formatFieldLabel(b)));
  }, [trackedChanges, section]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const clientId = location.pathname.split("/").pop();

        const [logsRes, profileRes] = await Promise.all([axios_tenant.get(`/kot/audit-logs?clientProfileId=${clientId}`), axios_tenant.get(`/kot/client-profiles/${clientId}`).catch(() => null)]);

        if (profileRes && profileRes.data) {
          const transformed = transformClientResponse(profileRes.data);
          setClientHeader({
            name: transformed?.clientDetails?.clientLegalName || transformed?.clientDetails?.clientName || "",
            number: transformed?.clientDetails?.clientNumber || "",
          });
        }

        const rawData = logsRes.data.content || [];
        const groupedMap = new Map();
        const finalData = [];

        const normalizeValForDisplay = (val) => {
          if (val === null || val === undefined) return null;
          if (typeof val === "string") {
            const trimmedVal = val.trim();
            if (trimmedVal.startsWith("{") || trimmedVal.startsWith("[")) return trimmedVal;
            return val;
          }
          if (typeof val === "object") {
            try {
              return JSON.stringify(val);
            } catch (e) {
              return String(val);
            }
          }
          return String(val);
        };

        const parseInnerJson = (str) => {
          if (!str) return str;
          if (typeof str === "string" && (str.startsWith("{") || str.startsWith("["))) {
            try {
              return JSON.parse(str);
            } catch (e) {
              return str;
            }
          }
          return str;
        };

        rawData.forEach((row) => {
          const isCreate = row.actionType === "CREATE";
          const isUpdate = row.actionType === "UPDATE";
          const isDelete = row.actionType === "DELETE";
          const isClientLevel = ["ClientDetail", "ClientDetails", "ClientProfile"].includes(row.sectionName);

          // CREATE: group into a single "Row Added" entry (drawer shows all fields)
          if (isCreate && row.sectionName) {
            // Group actions that happen within the same second for the same record
            const timeKey = row.changedAt ? row.changedAt.split(".")[0] : String(Date.now());
            const recordKey = row.recordId || "unknown_row";
            const key = `${row.sectionName}_${row.actionType}_${recordKey}_${timeKey}_${row.changedBy?.id}`;

            if (!groupedMap.has(key)) {
              const initialNewValueObj = row.fieldName ? { [row.fieldName]: parseInnerJson(row.newValue) } : parseInnerJson(row.newValue);

              const groupedRow = {
                ...row,
                id: key,
                actionType: isClientLevel ? "Client Added" : "Row Added",
                fieldName: isClientLevel ? row.fieldName || "Profile" : "Row",
                oldValueObj: null,
                newValueObj: initialNewValueObj,
                oldValue: "-",
              };
              groupedMap.set(key, groupedRow);
              finalData.push(groupedRow);
            } else {
              const existing = groupedMap.get(key);
              if (row.fieldName) {
                existing.newValueObj[row.fieldName] = parseInnerJson(row.newValue);
              } else {
                existing.newValueObj = Object.assign({}, existing.newValueObj, parseInnerJson(row.newValue));
              }
            }
            return;
          }

          // DELETE: show row-level delete with snapshot in Old Value
          if (isDelete && row.sectionName) {
            finalData.push({
              ...row,
              actionType: isClientLevel ? "Profile Deleted" : "Row Deleted",
              fieldName: isClientLevel ? row.fieldName || "Profile" : "Row",
              oldValue: normalizeValForDisplay(row.oldValue),
              newValue: normalizeValForDisplay(row.newValue),
            });
            return;
          }

          // UPDATE: do NOT group; show exact field and use "Field Updated" action
          if (isUpdate && row.sectionName) {
            finalData.push({
              ...row,
              actionType: "Field Updated",
              fieldName: row.fieldName || (isClientLevel ? "Profile" : "Row"),
              oldValue: normalizeValForDisplay(row.oldValue),
              newValue: normalizeValForDisplay(row.newValue),
            });
            return;
          }

          // Assignment changes now arrive as structured JSON values.
          // Preserve them so the drawer can render the before/after snapshot.
          if (row.actionType === "Assignment Changed") {
            finalData.push({
              ...row,
              fieldName: row.fieldName || "Assignment",
              oldValue: normalizeValForDisplay(row.oldValue),
              newValue: normalizeValForDisplay(row.newValue),
            });
            return;
          }

          if (row.actionType === "PRIVACY_CHANGE") {
            console.log("isPrivate", row);
            finalData.push({
              ...row,
              fieldName: "Confidentiality",
              actionType: "Access Level Changed",
              oldValue: row.oldValue === "false" ? "Public" : "Private",
              newValue: row.newValue === "false" ? "Public" : "Private",
            });
            return;
          }

          // Other actions (STATUS_CHANGE, DEACTIVATE, etc): keep as-is
          finalData.push({
            ...row,
            oldValue: normalizeValForDisplay(row.oldValue),
            newValue: normalizeValForDisplay(row.newValue),
          });
        });

        finalData.forEach((row) => {
          if (row.oldValueObj) {
            row.oldValue = JSON.stringify(row.oldValueObj);
            delete row.oldValueObj;
          }
          if (row.newValueObj) {
            row.newValue = JSON.stringify(row.newValueObj);
            delete row.newValueObj;
          }
        });

        setTrackedChanges(finalData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredRows = useMemo(() => {
    if (fromDate === "INVALID" || toDate === "INVALID") {
      return [];
    }

    return trackedChanges.filter((row) => {
      const matchesSection = section ? (section === "General Profile" ? !row.sectionName : row.sectionName === section) : true;
      const matchesField = field ? row.fieldName === field : true;

      const rowDate = row.changedAt ? new Date(row.changedAt) : null;
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      if (from) {
        from.setHours(0, 0, 0, 0);
      }

      if (to) {
        to.setHours(23, 59, 59, 999);
      }

      const matchesFromDate = from ? rowDate >= from : true;
      const matchesToDate = to ? rowDate <= to : true;

      return matchesSection && matchesField && matchesFromDate && matchesToDate;
    });
  }, [trackedChanges, section, field, fromDate, toDate]);

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const commonSx = {
    borderRadius: "10px",
    height: "44px",
    backgroundColor: "#fff",
  };

  const stripHtmlTags = (val) => {
    if (typeof val !== "string") return val;
    return val.replace(/<[^>]*>/g, "").trim();
  };

  const isHtmlContent = (val) => typeof val === "string" && /^\s*<[a-z][\s\S]*>/i.test(val);

  const renderJsonPreview = (val, rowMeta) => {
    if (typeof val === "string" && (val.startsWith("{") || val.startsWith("["))) {
      try {
        const parsed = JSON.parse(val);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          // Section-specific row preview (matches Track Changes spec)
          if (rowMeta?.sectionName === "Inventor") {
            const inventorLabel = parsed.fullLegalName || parsed.inventorName || parsed.name;
            if (inventorLabel) return <span style={{ fontWeight: 500 }}>{inventorLabel}</span>;
            return <span style={{ color: "gray", fontStyle: "italic", cursor: "inherit" }}>-</span>;
          }

          const compositePreview = (parsed.country && parsed.associateName ? `${parsed.country} - ${parsed.associateName}` : null) || (parsed.country && parsed.name ? `${parsed.country} - ${parsed.name}` : null) || (parsed.familyNo && parsed.contactName ? `${parsed.familyNo} - ${parsed.contactName}` : null);

          const previewName = compositePreview || parsed.clientName || parsed.clientLegalName || parsed.name || parsed.fullLegalName || parsed.firstAndLastName || (parsed.firstName && parsed.lastName ? `${parsed.firstName} ${parsed.lastName}` : parsed.firstName || parsed.lastName) || parsed.inventorName || parsed.contactName || parsed.associateName || parsed.faName || parsed.faCode || parsed.agentAttorneyName || parsed.agentName || parsed.organizationName || parsed.companyName || parsed.familyNo || parsed.country || parsed.correspondenceType || parsed.title || parsed.role;

          if (previewName) {
            return (
              <span style={{ cursor: "inherit", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontWeight: 500 }}>{previewName}</span>
              </span>
            );
          }
        }
      } catch (e) {
        // Ignore
      }
      return <span style={{ color: "gray", fontStyle: "italic", cursor: "inherit" }}>-</span>;
    }

    if (isHtmlContent(val)) {
      const text = stripHtmlTags(val);
      return <span>{text || <span style={{ color: "gray", fontStyle: "italic", cursor: "inherit" }}>-</span>}</span>;
    }
    return <span>{val}</span>;
  };

  const columns = [
    { field: "sectionName", headerName: "Section", flex: 1.5, renderCell: (params) => <span>{formatFormatStr(params.row.sectionName)}</span> },
    { field: "fieldName", headerName: "Field", flex: 1, renderCell: (params) => <span style={{ textTransform: "capitalize" }}>{formatFieldLabel(params.row.fieldName)}</span> },
    {
      field: "actionType",
      headerName: "Action",
      flex: 1.2,
      renderCell: (params) => {
        const isJson = (val) => typeof val === "string" && (val.startsWith("{") || val.startsWith("["));
        const hasJson = isJson(params.row.oldValue) || isJson(params.row.newValue);
        const isClickable = hasJson;
        return (
          <span
            onClick={() => isClickable && handleActionClick(params.row)}
            style={{
              color: isClickable ? "#1976d2" : "inherit",
              textDecoration: isClickable ? "underline" : "none",
              cursor: isClickable ? "pointer" : "default",
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      field: "oldValue",
      headerName: "Old Value",
      flex: 1,
      renderCell: (params) => {
        const val = params.row.oldValue;
        if (!val) return <span>-</span>;
        return renderJsonPreview(val, params.row);
      },
    },
    {
      field: "newValue",
      headerName: "New Value",
      flex: 1.2,
      renderCell: (params) => {
        const val = params.row.newValue;
        // Only say "Client Created" if it's the actual profile creation action
        if (params.row.actionType === "CREATE" && params.row.sectionName === "ClientDetail" && params.row.fieldName === "clientName") {
          return <span>Client Created</span>;
        }
        if (!val) return <span>-</span>;
        return renderJsonPreview(val, params.row);
      },
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      flex: 1,
      valueGetter: (params) => params.row.changedBy?.displayName || "",
      sortComparator: (v1, v2) => String(v1 || "").localeCompare(String(v2 || ""), undefined, { sensitivity: "base" }),
      renderCell: (params) => <span style={{ textTransform: "capitalize" }}>{params.row.changedBy?.displayName || "-"}</span>,
    },
    {
      field: "changedAt",
      headerName: "Updated Date",
      flex: 1,
      sortable: true,
      valueGetter: (params) => (params.row.changedAt ? new Date(params.row.changedAt).getTime() : 0),
      sortComparator: (v1, v2) => Number(v1 || 0) - Number(v2 || 0),
      renderCell: (params) => <span>{formatKotDate(params.row.changedAt)}</span>,
    },
  ];
  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h3" ml={1}>
          <TruncatedText text={displayTitle} maxLength={30} />
        </Typography>
      </Box>

      <Box
        p={5}
        sx={{
          height: "auto",
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">Track Changes</Typography>
          <Box display="flex" gap={2}>
            {/* Select Section */}
            <FormControl size="small">
              <Select value={section} onChange={(e) => setSection(e.target.value)} displayEmpty input={<OutlinedInput sx={commonSx} />} IconComponent={ArrowDropDownIcon} sx={{ minWidth: 180 }}>
                <MenuItem value="">Select Section</MenuItem>
                {availableSections.map((sec) => (
                  <MenuItem key={sec} value={sec}>
                    {formatFormatStr(sec)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Select Field */}
            <FormControl size="small">
              <Select value={field} onChange={(e) => setField(e.target.value)} displayEmpty input={<OutlinedInput sx={commonSx} />} IconComponent={ArrowDropDownIcon} sx={{ minWidth: 180 }}>
                <MenuItem value="">Select Field</MenuItem>
                {availableFields.map((f) => (
                  <MenuItem key={f} value={f}>
                    {formatFieldLabel(f)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* From Date */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="From"
                value={parseKotStoredDate(fromDate === "INVALID" ? "" : fromDate)}
                onChange={(newValue) => {
                  if (newValue === null) {
                    setFromDate("");
                  } else if (!newValue.isValid()) {
                    setFromDate("INVALID");
                  } else {
                    setFromDate(toKotStorageDate(newValue).toString());
                  }
                }}
                inputFormat={KOT_DISPLAY_DATE_FORMAT}
                renderInput={(pickerParams) => (
                  <TextField
                    {...pickerParams}
                    size="small"
                    sx={{ width: 180 }}
                    error={fromDate === "INVALID"}
                    onKeyDown={(e) => {
                      if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            {/* To Date */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="To"
                value={parseKotStoredDate(toDate === "INVALID" ? "" : toDate)}
                onChange={(newValue) => {
                  if (newValue === null) {
                    setToDate("");
                  } else if (!newValue.isValid()) {
                    setToDate("INVALID");
                  } else {
                    setToDate(toKotStorageDate(newValue).toString());
                  }
                }}
                inputFormat={KOT_DISPLAY_DATE_FORMAT}
                renderInput={(pickerParams) => (
                  <TextField
                    {...pickerParams}
                    size="small"
                    sx={{ width: 180 }}
                    error={toDate === "INVALID"}
                    onKeyDown={(e) => {
                      if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        {/* <Typography variant="h5" mt={3} color={"gray"}>
          These reflects the changes made in the past week. To view the complete change history, open Detail page and select "Track Changes".
        </Typography> */}
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 2,
            mt: 3,
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            getRowHeight={() => "auto"}
            sx={{
              border: 0,

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f7fb",
                fontWeight: "bold",
                borderBottom: "1px solid #ddd",
              },

              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #eee",
                whiteSpace: "normal",
                wordBreak: "break-word",
                py: 1,
              },

              "& .MuiDataGrid-columnSeparator": {
                visibility: "visible",
              },
              // Custom pagination styles
              ".MuiTablePagination-displayedRows": {
                marginTop: "1em",
                marginBottom: "1em",
              },
              ".MuiTablePagination-selectLabel": {
                marginTop: "1em",
                marginBottom: "1em",
              },
            }}
          />
        </Box>
        <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
          <Box width={500} p={3}>
            <Typography variant="h3" mb={2}>
              Change Details
            </Typography>

            {selectedRow &&
              (() => {
                const tryParse = (val) => {
                  if (!val) return null;
                  try {
                    const parsed = JSON.parse(val);
                    return typeof parsed === "object" ? parsed : val;
                  } catch {
                    return val;
                  }
                };

                const parsedOldVal = tryParse(selectedRow.oldValue);
                const parsedNewVal = tryParse(selectedRow.newValue);

                const renderValue = (val, depth = 0) => {
                  if (val === null || val === undefined) return <Typography color="text.secondary">-</Typography>;

                  if (Array.isArray(val)) {
                    if (val.length === 0) return <Typography color="text.secondary">Empty List</Typography>;
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: depth > 0 ? 2 : 0, mt: depth > 0 ? 1 : 0 }}>
                        {val.map((item, index) => (
                          <Box key={index} sx={{ bgcolor: depth === 0 ? "#f9fafc" : "transparent", p: depth === 0 ? 1.5 : 0, borderRadius: 2, border: depth === 0 ? "1px solid #e0e0e0" : "none" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                              Item {index + 1}
                            </Typography>
                            {renderValue(item, depth + 1)}
                          </Box>
                        ))}
                      </Box>
                    );
                  }

                  if (typeof val === "object") {
                    return (
                      <Box sx={{ bgcolor: depth === 0 ? "#f9fafc" : "transparent", p: depth === 0 ? 2 : 0, borderRadius: 2, border: depth === 0 ? "1px solid #e0e0e0" : "none", mt: depth === 0 ? 1 : 0, width: "100%" }}>
                        {Object.entries(val).map(([k, v]) => (
                          <Box key={k} display="flex" mb={0.5} flexDirection={typeof v === "object" && v !== null ? "column" : "row"}>
                            <Typography sx={{ width: typeof v === "object" && v !== null ? "100%" : 200, fontWeight: 600, color: "#555", fontSize: "0.85rem", mb: typeof v === "object" && v !== null ? 0.5 : 0 }}>{formatFieldLabel(k)}:</Typography>
                            <Box sx={{ flex: 1, wordBreak: "break-word", fontSize: "0.85rem" }}>
                              {v === null || v === "" ? (
                                <Typography sx={{ fontSize: "0.85rem" }}>-</Typography>
                              ) : ["lastUpdatedOn", "createdAt", "updatedAt"].includes(k) ? (
                                <Typography sx={{ fontSize: "0.85rem" }}>{formatKotDateTime(v)}</Typography>
                              ) : selectedRow.fieldName === "documentUrls" && k === "url" ? (
                                <a
                                  href={v}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {v}
                                </a>
                              ) : (
                                <Typography sx={{ fontSize: "0.85rem" }}>{isHtmlContent(v) ? stripHtmlTags(v) : String(v)}</Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  }

                  const display = isHtmlContent(val) ? stripHtmlTags(val) : String(val);
                  return (
                    <Typography sx={{ wordBreak: "break-word" }}>
                      {display || (
                        <Typography component="span" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Typography>
                  );
                };

                return (
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Typography color="text.secondary" variant="caption" fontWeight="bold">
                          ACTION
                        </Typography>
                        <Typography color="primary.main">{selectedRow.actionType}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography color="text.secondary" variant="caption" fontWeight="bold">
                          SECTION
                        </Typography>
                        <Typography>{formatFormatStr(selectedRow.sectionName)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography color="text.secondary" variant="caption" fontWeight="bold">
                          UPDATED BY
                        </Typography>
                        <Typography>{selectedRow.changedBy?.displayName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography color="text.secondary" variant="caption" fontWeight="bold">
                           UPDATED ON
                        </Typography>
                        <Typography>{formatKotDateTime(selectedRow.changedAt)}</Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3, borderColor: "#e0e0e0", borderBottomWidth: 2 }} />

                    <Box mb={4}>
                      <Typography variant="subtitle1" color="error.main" mb={1}>
                        {selectedRow.actionType === "CREATE" ? "Previous Value (None)" : "Old Value"}
                      </Typography>
                      {renderValue(parsedOldVal)}
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" color="success.main" mb={1}>
                        New Value
                      </Typography>
                      {renderValue(parsedNewVal)}
                    </Box>
                  </Box>
                );
              })()}
          </Box>
        </Drawer>
      </Box>
    </Box>
  );
};

export default ClientChanges;
